<template>
    <div v-if="result && statement" class="es-preview">
        <!-- Superusers can also see the employer's side (employer taxes and contributions), and
             what went into and came out of the net pay engine -->
        <div v-if="isSuperuser" class="d-flex justify-content-end mb-2">
            <div class="btn-group btn-group-sm" role="group">
                <button
                    v-for="mode in viewModes"
                    :key="mode.value"
                    type="button"
                    class="btn"
                    :class="viewMode === mode.value ? 'btn-primary' : 'btn-outline-primary'"
                    @click.prevent="viewMode = mode.value"
                >
                    {{ mode.label }}
                </button>
            </div>
        </div>

        <!-- Engine I/O: the PaycheckInput and PaycheckResult, as the engine took and returned them -->
        <section v-if="viewMode === 'engine'" class="es-section es-engine-io">
            <div v-for="panel in engineIoPanels" :key="panel.key" class="es-engine-panel">
                <div class="d-flex justify-content-between align-items-center">
                    <a href="#" class="es-ytd-toggle" @click.prevent="toggleEnginePanel(panel.key)">
                        <i class="far fa-fw" :class="openEnginePanels[panel.key] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                        {{ panel.title }}
                    </a>
                    <button type="button" class="btn btn-sm btn-link" @click.prevent="$bus.copyToClipboard(panel.json)">
                        <i class="far fa-fw fa-copy"></i> Copy
                    </button>
                </div>
                <pre v-if="openEnginePanels[panel.key]" class="es-engine-json">{{ panel.json }}</pre>
            </div>
        </section>

        <!-- Company / Employee -->
        <div class="row">
            <div class="col-6">
                <h4 class="es-block-title">Company</h4>
                <p class="es-address">
                    {{ company.name }}<br/>
                    <span v-if="company.address_1">{{ company.address_1 }}<br/></span>
                    <span v-if="company.address_2">{{ company.address_2 }}<br/></span>
                    <span v-if="company.city">{{ company.city }}, {{ company.state }} {{ company.postal_code }}</span>
                </p>
            </div>
            <div class="col-6">
                <h4 class="es-block-title">Employee</h4>
                <p class="es-address">
                    {{ employee.full_name }}<br/>
                    <span v-if="employee.address_1">{{ employee.address_1 }}<br/></span>
                    <span v-if="employee.address_2">{{ employee.address_2 }}<br/></span>
                    <span v-if="employee.city">{{ employee.city }}, {{ employee.state }} {{ employee.postal_code }}</span>
                </p>
            </div>
        </div>

        <!-- Check date / pay period -->
        <div class="es-band es-band-dates row">
            <div :class="dateColumnClass">
                <div class="es-label">Check Date</div>
                <div>{{ $filters.date(statement.paygroup_run.check_date, 'ddd, MMM D YYYY') }}</div>
            </div>
            <div :class="dateColumnClass">
                <div class="es-label">Pay Period</div>
                <div>{{ $filters.date(statement.paygroup_run.pay_period_start_date, 'MMM D') }} - {{ $filters.date(statement.paygroup_run.pay_period_end_date, 'MMM D, YYYY') }}</div>
            </div>
            <!-- Only a processed EarningsStatement has a number -->
            <div v-if="statement.earning_statement_number" :class="dateColumnClass">
                <div class="es-label">Statement Number</div>
                <div>{{ statement.earning_statement_number }}</div>
            </div>
        </div>

        <!-- Withholding elections -->
        <div v-if="withholdingBlocks.length" class="row">
            <div v-for="block in withholdingBlocks" :key="block.id" class="col-6 mb-2">
                <h4 class="es-block-title">{{ block.title }}</h4>
                <div v-for="line in block.lines" :key="line.label" class="es-withholding-line">
                    <strong>{{ line.label }}:</strong> {{ line.value }}
                </div>
            </div>
        </div>

        <!-- Current Earnings -->
        <section class="es-section">
            <h4 v-if="!positionsWithCurrentEarnings.length" class="es-section-title"><i class="far fa-fw fa-usd-circle"></i> Current Earnings</h4>
            <div v-for="(position, index) in positionsWithCurrentEarnings" :key="position.id" class="es-position">
                <!-- The section title shares its line with the first position's pill -->
                <div class="es-position-header" :class="{'es-position-header-first': index == 0}">
                    <h4 v-if="index == 0" class="es-section-title"><i class="far fa-fw fa-usd-circle"></i> Current Earnings</h4>
                    <span class="es-pill">{{ position.label }}</span>
                </div>
                <table class="es-table">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th class="text-right">Rate</th>
                            <th class="text-right">Hours</th>
                            <th class="text-right">Units</th>
                            <th class="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(earning, i) in position.earnings" :key="i">
                            <td>
                                {{ earningName(earning) }}
                                <i v-if="isSupplemental(earning)" class="far fa-fw fa-badge-percent text-primary ml-1" title="Supplemental tax rate"></i>
                            </td>
                            <td class="text-right">
                                <span v-if="earning.calculated_rate && (earning.hours || earning.units)">{{ $filters.dollars(earning.calculated_rate) }}</span>
                            </td>
                            <td class="text-right">{{ earningHours(earning) }}</td>
                            <td class="text-right">{{ earningUnits(earning) }}</td>
                            <td class="text-right" :class="{'text-primary': isSupplemental(earning)}">{{ $filters.dollars(earning.calculated_amount || 0) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Total</td>
                            <td></td>
                            <td class="text-right">{{ positionTotals(position).hours }}</td>
                            <td class="text-right">{{ positionTotals(position).units }}</td>
                            <td class="text-right">{{ $filters.dollars(positionTotals(position).amount) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </section>

        <div class="es-band es-band-gross d-flex justify-content-between">
            <span>Gross Pay</span>
            <span>{{ $filters.dollars(result.gross_pay) }}</span>
        </div>

        <!-- Current Reimbursements (not part of gross pay; added to net pay) -->
        <section v-if="reimbursements.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-receipt"></i> Current Reimbursements</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(reimbursement, i) in reimbursements" :key="i">
                        <td>{{ reimbursement.name }}</td>
                        <td class="text-right">{{ $filters.dollars(reimbursement.actual_amount) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total</td>
                        <td class="text-right">{{ $filters.dollars(result.reimbursements_total) }}</td>
                    </tr>
                </tfoot>
            </table>
        </section>

        <!-- Current Taxes -->
        <section class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-badge-percent"></i> Current Taxes</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(tax, i) in currentTaxes" :key="i">
                        <td>{{ taxName(tax) }}</td>
                        <td class="text-right">{{ $filters.dollars(tax.withheld_amount) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total</td>
                        <td class="text-right">{{ $filters.dollars(result.tax_total) }}</td>
                    </tr>
                </tfoot>
            </table>
        </section>

        <!-- Current Employer Taxes (advanced view) -->
        <section v-if="showEmployerDetail && currentEmployerTaxes.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-badge-percent"></i> Current Employer Taxes</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(tax, i) in currentEmployerTaxes" :key="i">
                        <td>{{ taxName(tax) }}</td>
                        <td class="text-right">{{ $filters.dollars(tax.withheld_amount) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total</td>
                        <td class="text-right">{{ $filters.dollars(result.employer_tax_total) }}</td>
                    </tr>
                </tfoot>
            </table>
        </section>

        <!-- Current Benefits -->
        <section v-if="benefits.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-file-plus"></i> Current Benefits</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th v-if="showEmployerDetail" class="text-right">ER Contribution</th>
                        <th class="text-right">EE Contribution</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="benefit in benefits" :key="benefit.key">
                        <td>{{ benefit.name }}</td>
                        <td v-if="showEmployerDetail" class="text-right">{{ $filters.dollars(benefit.employer_amount) }}</td>
                        <td class="text-right">{{ $filters.dollars(benefit.amount) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total</td>
                        <td v-if="showEmployerDetail" class="text-right">{{ $filters.dollars(benefitsEmployerTotal) }}</td>
                        <td class="text-right">{{ $filters.dollars(benefitsEmployeeTotal) }}</td>
                    </tr>
                </tfoot>
            </table>
        </section>

        <!-- Current Deductions -->
        <section v-if="deductions.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-funnel-dollar"></i> Current Deductions</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(deduction, i) in deductions" :key="i">
                        <td>{{ deduction.name }}</td>
                        <td class="text-right">{{ $filters.dollars(deduction.actual_amount) }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <td>Total</td>
                        <td class="text-right">{{ $filters.dollars(deductionsTotal) }}</td>
                    </tr>
                </tfoot>
            </table>
        </section>

        <!-- Time Off: what this check would post to each accrual plan (accruals.paygrid.projected_paystub_rows) -->
        <section v-if="accruals.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-calendar-check"></i> Time Off</h4>
            <table class="es-table">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th class="text-right">Accrued Hours</th>
                        <th class="text-right">Used Hours</th>
                        <th v-if="accrualsPaidOut" class="text-right">Paid Out Hours</th>
                        <th class="text-right">Balance Hours</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="accrual in accruals" :key="accrual.employee_accrual_plan_id">
                        <td>{{ accrual.display_name }}</td>
                        <td class="text-right">{{ hours(accrual.accrued) }}</td>
                        <td class="text-right">{{ hours(accrual.used) }}</td>
                        <td v-if="accrualsPaidOut" class="text-right">{{ hours(accrual.paid_out) }}</td>
                        <td class="text-right">{{ hours(accrual.balance) }}</td>
                    </tr>
                </tbody>
            </table>
        </section>

        <!-- Net Pay -->
        <div class="es-band es-band-net">
            <div class="d-flex justify-content-between es-net-total">
                <span>Net Pay</span>
                <span>{{ $filters.dollars(result.net_pay) }}</span>
            </div>
            <div v-if="showPayout && payoutDetails.length" class="mt-1">
                <div class="es-label mb-1">Payout Breakdown</div>
                <table class="es-table es-payout-table">
                    <tbody>
                        <tr v-for="(row, idx) in payoutDetails" :key="idx">
                            <td>
                                <span class="es-dot" :class="row.method === 'check' ? 'es-dot-check' : 'es-dot-dd'"></span>
                                {{ row.method === 'check' ? 'Check' : 'Direct deposit' }}
                            </td>
                            <td>{{ row.destination }}</td>
                            <td class="text-right">${{ row.amount }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Notes -->
        <section v-if="notes.length" class="es-section">
            <h4 class="es-section-title"><i class="far fa-fw fa-comment-alt-lines"></i> Notes</h4>
            <div class="es-notes">
                <p v-for="(note, i) in notes" :key="i" class="mb-0">{{ note }}</p>
            </div>
        </section>

        <!-- Year-to-date -->
        <div v-if="showYtd && ytd" class="es-ytd">
            <a href="#" class="es-ytd-toggle" @click.prevent="ytdOpen = !ytdOpen">
                <i class="far fa-fw" :class="ytdOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                Year-to-date Summary
            </a>

            <div v-if="ytdOpen">
                <!-- YTD Earnings -->
                <section class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-usd-circle"></i> Year-to-date Earnings</h4>
                    <div v-for="p in positionsWithYtdEarningsSorted" :key="p.id" class="es-position">
                        <div class="text-right mb-1"><span class="es-pill">{{ p.label }}</span></div>
                        <table class="es-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th class="text-right">Rate</th>
                                    <th class="text-right">Hours</th>
                                    <th class="text-right">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(earning, i) in ytdEarningsByPositionId[p.id].earnings" :key="i">
                                    <td>{{ ytdEarningName(earning) }}</td>
                                    <td class="text-right">
                                        <span v-if="earning.rate && earning.earning_type_type !== 'amount'">{{ $filters.dollars(earning.rate) }}</span>
                                    </td>
                                    <td class="text-right">{{ earning.earning_type_type === 'amount' ? '' : earning.hours }}</td>
                                    <td class="text-right">{{ $filters.dollars(earning.amount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="es-band es-band-gross d-flex justify-content-between">
                        <span>YTD Gross Pay</span>
                        <span>{{ $filters.dollars(ytdEarningsTotal) }}</span>
                    </div>
                </section>

                <!-- YTD Reimbursements -->
                <section v-if="ytdReimbursements.length" class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-receipt"></i> Year-to-date Reimbursements</h4>
                    <table class="es-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th class="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(reimbursement, i) in ytdReimbursements" :key="i">
                                <td>{{ reimbursement.name }}</td>
                                <td class="text-right">{{ $filters.dollars(reimbursement.amount) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td class="text-right">{{ $filters.dollars(ytd.totals.reimbursements) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <!-- YTD Taxes -->
                <section class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-badge-percent"></i> Year-to-date Taxes</h4>
                    <table class="es-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th class="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(tax, i) in symTaxesWithYtd" :key="i">
                                <td>{{ tax.description.toUpperCase() }}</td>
                                <td class="text-right">{{ $filters.dollars(ytdItemAmounts.taxes[tax.id]) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td class="text-right">{{ $filters.dollars(ytdTaxesTotal) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <!-- YTD Employer Taxes (advanced view) -->
                <section v-if="showEmployerDetail && ytdEmployerTaxes.length" class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-badge-percent"></i> Year-to-date Employer Taxes</h4>
                    <table class="es-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th class="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="tax in ytdEmployerTaxes" :key="tax.sym_tax_id">
                                <td>{{ tax.description.toUpperCase() }}</td>
                                <td class="text-right">{{ $filters.dollars(tax.amount) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td class="text-right">{{ $filters.dollars(ytdEmployerTaxesTotal) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <!-- YTD Benefits -->
                <section v-if="ytdBenefits.length" class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-file-plus"></i> Year-to-date Benefits</h4>
                    <table class="es-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th v-if="showYtdEmployerContributions" class="text-right">ER Contribution</th>
                                <th class="text-right">EE Contribution</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="benefit in ytdBenefits" :key="benefit.key">
                                <td>{{ benefit.name }}</td>
                                <td v-if="showYtdEmployerContributions" class="text-right">{{ $filters.dollars(benefit.employer_amount) }}</td>
                                <td class="text-right">{{ $filters.dollars(benefit.amount) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td v-if="showYtdEmployerContributions" class="text-right">{{ $filters.dollars(ytdBenefitsEmployerTotal) }}</td>
                                <td class="text-right">{{ $filters.dollars(ytdBenefitsEmployeeTotal) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <!-- YTD Deductions (deductions that are not benefit plans) -->
                <section v-if="miscDeductionsWithYtd.length" class="es-section">
                    <h4 class="es-section-title"><i class="far fa-fw fa-funnel-dollar"></i> Year-to-date Deductions</h4>
                    <table class="es-table">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th class="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="deduction in miscDeductionsWithYtd" :key="'misc-' + deduction.id">
                                <td>{{ deduction.company_deduction_type.deduction_type.name }}</td>
                                <td class="text-right">{{ $filters.dollars(ytdItemAmounts.misc[deduction.id]) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td class="text-right">{{ $filters.dollars(ytdDeductionsTotal) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </section>

                <!-- YTD Net Pay -->
                <div class="es-band es-band-net d-flex justify-content-between es-net-total">
                    <span>YTD Net Pay</span>
                    <span>{{ $filters.dollars(ytdNet) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { makePositionSlug } from '@/utils/payroll'
import { visibleParameters, formatParamValue } from '@/utils/symTaxConfig'

export default {
    props: {
        client: { required: true },
        employee: { required: true },
        statement: { required: true },        // PaygridEarningsStatement (with paygroup_run)
        result: { required: true },           // earnings_statement_result from the net pay engine
        paychecks: { default: () => [] },
        // WorkLocations that label each position, for a statement shown without its paychecks
        // (a processed EarningsStatement); the paychecks' own locations are used as well
        workLocations: { default: () => [] },
        ytd: { default: null },               // required when showYtd is true
        showPayout: { type: Boolean, default: false },
        showYtd: { type: Boolean, default: false },
        // Applied blocked selections, used to label blocked lines "Not Taxed"/"Not Deducted" (which still display at $0)
        blockedDeductions: { default: () => ({}) },  // { category: [ids] }
        blockedTaxes: { default: () => [] },          // [unique_tax_id]
        // employee.sym_tax_config from the pay grid employee endpoint: the Symmetry parameters
        // the net pay engine taxed this statement with. Empty or null hides the block.
        taxConfigs: { default: null },
        // The client's CompanyEarningTypes (with earning_type), which name each earning
        companyEarningTypes: { default: () => [] },
        // { input, result }: the net pay engine's PaycheckInput and PaycheckResult, which the
        // server sends only to superusers. Null hides the Engine I/O view.
        engineIo: { default: null },
    },
    data() {
        return {
            ytdOpen: false,
            viewMode: 'employee',  // 'employee', 'advanced' or 'engine'
            openEnginePanels: { input: true, result: true },
        }
    },
    computed: {
        isSuperuser() {
            return Boolean(this.$store.state.user && this.$store.state.user.is_superuser)
        },
        viewModes() {
            const modes = [
                { value: 'employee', label: 'Employee View' },
                { value: 'advanced', label: 'Advanced View' },
            ]
            if (this.engineIo) {
                modes.push({ value: 'engine', label: 'Engine I/O' })
            }
            return modes
        },
        // Employer taxes and employer contributions show only in a superuser's advanced view,
        // which the Engine I/O view includes
        showEmployerDetail() {
            return this.isSuperuser && this.viewMode !== 'employee'
        },
        engineIoPanels() {
            if (!this.engineIo) return []
            return [
                { key: 'input', title: 'Engine Input (PaycheckInput)', json: JSON.stringify(this.engineIo.input, null, 2) },
                { key: 'result', title: 'Engine Result (PaycheckResult)', json: JSON.stringify(this.engineIo.result, null, 2) },
            ]
        },
        company() {
            if (this.statement) {
                return this.client.companies.find(c => c.id == this.statement.company_id) || {}
            }
            return {}
        },
        workLocationsById() {
            const byId = {}
            for (const workLocation of this.workLocations) {
                byId[workLocation.id] = workLocation
            }
            for (const paycheck of this.paychecks) {
                byId[paycheck.work_location_id] = paycheck.work_location
            }
            return byId
        },
        dateColumnClass() {
            return this.statement.earning_statement_number ? 'col-4' : 'col-6'
        },
        companyEarningTypesById() {
            return (this.companyEarningTypes || []).reduce((acc, cet) => {
                acc[cet.id] = cet
                return acc
            }, {})
        },
        positionsWithCurrentEarnings() {
            if (!this.result) return []
            const byPosition = this.result.earnings.reduce((acc, earning) => {
                const posId = earning.identifiers.position_id
                if (!acc[posId]) {
                    acc[posId] = { id: posId, label: this.positionLabel(earning.identifiers), earnings: [] }
                }
                acc[posId].earnings.push(earning)
                return acc
            }, {})
            // $0 earnings (such as a position default nothing was entered for) show only in the
            // advanced view, as with taxes and deductions; filtered after overtime lines combine
            return Object.values(byPosition)
                .map(position => ({
                    id: position.id,
                    label: position.label,
                    earnings: this.orderedPositionEarnings(position.earnings)
                        .filter(earning => this.showEmployerDetail || Number(earning.calculated_amount || 0) !== 0),
                }))
                .filter(position => position.earnings.length)
                .sort((a, b) => a.label.localeCompare(b.label))
        },
        currentTaxes() {
            if (!this.result) return []
            // Blocked ("not taxed") lines are hidden rather than shown at $0, and $0 ones show
            // only in the advanced view. Kept in the server's order (SymTax.sort_priority).
            return this.result.taxes
                .filter(t => !t.is_employer_tax && !this.isTaxBlocked(t))
                .filter(t => this.showEmployerDetail || parseFloat(t.withheld_amount) !== 0)
        },
        currentEmployerTaxes() {
            if (!this.result) return []
            // Kept in the server's order (SymTax.sort_priority)
            return this.result.taxes
                .filter(t => t.is_employer_tax && !this.isTaxBlocked(t))
        },
        deductions() {
            // Deductions that are not benefit plans (see benefits). Hide blocked ("not deducted")
            // deductions, and $0 ones outside the advanced view, then alphabetical
            return [...(this.result?.deductions || [])]
                .filter(d => !this.isBenefit(d) && !this.isDeductionBlocked(d))
                .filter(d => this.showEmployerDetail || parseFloat(d.actual_amount) !== 0)
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        deductionsTotal() {
            return this.deductions.reduce((sum, d) => sum + parseFloat(d.actual_amount || 0), 0)
        },
        reimbursements() {
            return (this.result?.reimbursements || [])
                .filter(r => parseFloat(r.actual_amount) !== 0)
        },
        accruals() {
            return this.result?.accruals || []
        },
        accrualsPaidOut() {
            return this.accruals.some(accrual => Number(accrual.paid_out))
        },
        deductionNotes() {
            return (this.result?.deductions || [])
                .filter(d => !this.isDeductionBlocked(d) && d.note)
                .map(d => d.note)
        },
        benefits() {
            // Retirement, insurance and other benefit plans: the employee's withheld amount beside
            // the employer's contribution, which the engine returns separately in `calculations`
            const employerAmounts = {}
            for (const calculation of this.result?.calculations || []) {
                const key = this.benefitKey(calculation.identifiers)
                if (key) {
                    employerAmounts[key] = (employerAmounts[key] || 0) + parseFloat(calculation.amount || 0)
                }
            }
            const rows = (this.result?.deductions || [])
                .filter(d => this.isBenefit(d) && !this.isDeductionBlocked(d))
                .map(d => {
                    const key = this.benefitKey(d.identifiers)
                    return {
                        key: `${key}:${d.is_catch_up ? 'catch-up' : 'base'}`,
                        name: d.name,
                        amount: parseFloat(d.actual_amount || 0),
                        // One match per enrollment, shown on its base contribution rather than the catch-up
                        employer_amount: d.is_catch_up ? 0 : (employerAmounts[key] || 0),
                    }
                })
            // Outside the advanced view only what the employee pays shows, so $0 rows (plans the
            // employer pays in full among them) are hidden; the advanced view shows every plan
            return rows
                .filter(row => this.showEmployerDetail || row.amount !== 0)
                .sort((a, b) => a.name.localeCompare(b.name))
        },
        benefitsEmployerTotal() {
            return this.benefits.reduce((sum, row) => sum + row.employer_amount, 0)
        },
        benefitsEmployeeTotal() {
            return this.benefits.reduce((sum, row) => sum + row.amount, 0)
        },
        payoutDetails() {
            if (!this.result) return []
            const netPay = parseFloat(this.result.net_pay) || 0
            const accounts = (this.employee && this.employee.direct_deposit_accounts) || []
            // Show a check row if no DD accounts or if this is a separate/bonus check
            if (!accounts.length || this.paychecks[0]?.is_separate_check) {
                return [{ method: 'check', destination: '', amount: netPay.toFixed(2) }]
            }
            // Process fixed and percentage first, remainder last
            const nonRemainder = accounts.filter(a => a.split_type !== 'remainder')
            const remainder = accounts.filter(a => a.split_type === 'remainder')
            let remaining = netPay
            const rows = []
            for (const acct of nonRemainder) {
                let amount
                if (acct.split_type === 'fixed') {
                    amount = Math.min(parseFloat(acct.amount) || 0, remaining)
                } else if (acct.split_type === 'percentage') {
                    amount = parseFloat(((acct.percentage / 100) * netPay).toFixed(2))
                } else {
                    amount = 0
                }
                remaining = parseFloat((remaining - amount).toFixed(2))
                rows.push({ method: 'dd', destination: `${acct.bank_name} (...${acct.account_number_last_4})`, amount: amount.toFixed(2) })
            }
            for (const acct of remainder) {
                rows.push({ method: 'dd', destination: `${acct.bank_name} (...${acct.account_number_last_4})`, amount: remaining.toFixed(2) })
            }
            return rows
        },
        // --- YTD (only used when showYtd) ---
        ytdEarningsByPositionId() {
            // Same rule as the current earnings: $0 rows show only in the advanced view
            const byPositionId = {}
            for (const [positionId, position] of Object.entries(this.ytd?.combined_earnings_by_position_id || {})) {
                byPositionId[positionId] = {
                    ...position,
                    earnings: position.earnings
                        .filter(earning => this.showEmployerDetail || Number(earning.amount || 0) !== 0),
                }
            }
            return byPositionId
        },
        symTaxesWithYtd() {
            // $0 rows show only in the advanced view, as with the current taxes
            return (this.ytd?.sym_taxes || [])
                .filter(tax => this.showEmployerDetail || parseFloat(this.ytdItemAmounts.taxes?.[tax.id] || 0) !== 0)
        },
        ytdBenefits() {
            // Same rule as benefits: $0 rows show only in the advanced view
            return (this.ytd?.benefits || [])
                .filter(row => this.showEmployerDetail || parseFloat(row.amount) !== 0)
        },
        ytdEmployerTaxes() {
            return this.ytd?.employer_taxes || []
        },
        ytdEmployerTaxesTotal() {
            return this.ytdEmployerTaxes.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
        },
        // A processed statement's YTD has no employer contributions (employer_amount null)
        showYtdEmployerContributions() {
            return this.showEmployerDetail && this.ytdBenefits.every(row => row.employer_amount != null)
        },
        ytdBenefitsEmployerTotal() {
            return this.ytdBenefits.reduce((sum, row) => sum + parseFloat(row.employer_amount || 0), 0)
        },
        ytdBenefitsEmployeeTotal() {
            return this.ytdBenefits.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
        },
        miscDeductionsWithYtd() {
            // $0 rows show only in the advanced view, as with the current deductions
            return (this.ytd?.misc_deductions || [])
                .filter(deduction => this.showEmployerDetail || parseFloat(this.ytdItemAmounts.misc?.[deduction.id] || 0) !== 0)
        },
        ytdItemAmounts() {
            return this.ytd?.item_amounts || {}
        },
        ytdEarningsTotal() {
            return this.ytd?.totals?.earnings
        },
        ytdTaxesTotal() {
            return this.ytd?.totals?.taxes
        },
        ytdDeductionsTotal() {
            if (!this.ytd) return 0
            return parseFloat(this.ytd.totals.misc_deductions)
        },
        ytdNet() {
            return this.ytd?.totals?.net
        },
        ytdReimbursements() {
            return (this.ytd?.reimbursements || [])
                .filter(r => parseFloat(r.amount) !== 0)
        },
        positionsWithYtdEarningsSorted() {
            const positions = this.ytd?.positions_with_combined_earnings
            if (!positions) return []
            return positions
                .filter(p => this.ytdEarningsByPositionId[p.id]?.earnings.length)
                .map(p => ({ ...p, label: `${p.work_location.name} / ${p.slug}` }))
                .sort((a, b) => a.label.localeCompare(b.label))
        },
        withholdingBlocks() {
            // One block per tax the employee has elections on (income taxes in practice; FICA and
            // Medicare carry no employee-level parameters and drop out)
            return (this.taxConfigs || []).map(taxConfig => {
                const lines = visibleParameters(taxConfig).map(param => ({
                    label: param.description,
                    value: formatParamValue(taxConfig, param),
                }))
                if (taxConfig.additional_wh) {
                    lines.push({ label: 'Add. Withholding', value: this.$filters.dollars(taxConfig.additional_wh) })
                }
                if (taxConfig.is_exempt) {
                    lines.push({ label: 'Exempt', value: 'Yes' })
                }
                return { id: taxConfig.id, title: taxConfig.sym_tax.description, lines: lines }
            }).filter(block => block.lines.length)
        },
        notes() {
            // One note per supplemental earning, named with its position, e.g. "BETHS CHICAGO / FOH-CREW BONUS"
            const supplementalNames = new Set(
                (this.result?.earnings || [])
                    .filter(earning => this.isSupplemental(earning))
                    .map(earning => `${this.positionLabel(earning.identifiers)} ${this.earningName(earning)}`)
            )
            const supplementalNotes = Array.from(supplementalNames)
                .map(name => `Supplemental tax rate used on ${name} earning`)
            return supplementalNotes.concat(this.deductionNotes)
        },
    },
    methods: {
        hours(value) {
            return Number(value).toFixed(2)
        },
        toggleEnginePanel(key) {
            this.openEnginePanels[key] = !this.openEnginePanels[key]
        },
        // Taxed at the supplemental rate (WageType in payroll/models/earnings.py)
        isSupplemental(earning) {
            return earning.wage_type === 'Supplemental' || earning.wage_type === 'PreviousPeriodSupplemental'
        },
        // Symmetry splits income taxes into a Regular and a Supplemental row on a check
        // with supplemental wages; both rows share the tax's name so label the supplemental
        taxName(tax) {
            const name = tax.name.toUpperCase()
            return this.isSupplemental(tax) ? `${name} (SUPPLEMENTAL)` : name
        },
        // Identifies the plan enrollment a deduction or an employer contribution belongs to;
        // null for deductions that are not benefit plans
        benefitKey(identifiers) {
            const ids = identifiers || {}
            if (ids.employee_deferred_comp_id != null) {
                return `deferred-comp:${ids.employee_deferred_comp_id}`
            }
            if (ids.employee_insurance_plan_id != null) {
                return `insurance:${ids.employee_insurance_plan_id}`
            }
            if (ids.employee_other_benefit_deduction_id != null) {
                return `other-benefit:${ids.employee_other_benefit_deduction_id}`
            }
            return null
        },
        isBenefit(deduction) {
            return this.benefitKey(deduction.identifiers) !== null
        },
        isTaxBlocked(tax) {
            return (this.blockedTaxes || []).some(id => String(id) === String(tax.tax_id))
        },
        isDeductionBlocked(deduction) {
            const ids = deduction.identifiers || {}
            const blocked = this.blockedDeductions || {}
            const inList = (list, id) => (list || []).some(x => String(x) === String(id))
            if (ids.employee_deferred_comp_id != null) {
                return inList(blocked.deferred_comps, ids.employee_deferred_comp_id)
            }
            if (ids.employee_misc_deduction_id != null) {
                return inList(blocked.misc_deductions, ids.employee_misc_deduction_id)
            }
            return false
        },
        orderedPositionEarnings(earnings) {
            // Combine overtime lines in this position that share a rate (sum hours + amount)
            const ordered = []
            const overtimeByRate = {}
            earnings.forEach(earning => {
                if (!earning.is_overtime) {
                    ordered.push(earning)
                    return
                }
                const rateKey = String(earning.calculated_rate)
                const existing = overtimeByRate[rateKey]
                if (existing) {
                    existing.hours = Number(existing.hours || 0) + Number(earning.hours || 0)
                    existing.calculated_amount = Number(existing.calculated_amount || 0) + Number(earning.calculated_amount || 0)
                } else {
                    const merged = {
                        ...earning,
                        hours: Number(earning.hours || 0),
                        calculated_amount: Number(earning.calculated_amount || 0),
                    }
                    overtimeByRate[rateKey] = merged
                    ordered.push(merged)
                }
            })
            // Order: regular, then overtime, then everything else alphabetically by earning name
            const rank = earning => {
                const cet = this.companyEarningTypesById[earning.identifiers.company_earning_type_id]
                if (cet?.earning_type?.slug === 'regular') return 0
                if (earning.is_overtime) return 1
                return 2
            }
            return ordered.sort((a, b) => {
                const rankDiff = rank(a) - rank(b)
                if (rankDiff !== 0) return rankDiff
                if (rank(a) !== 2) return 0
                return (this.earningName(a) || '').localeCompare(this.earningName(b) || '')
            })
        },
        positionLabel(identifiers) {
            const workLocation = this.workLocationsById[identifiers.work_location_id]?.name
            return `${workLocation} / ${makePositionSlug(this.client.org_level_after_location, identifiers.org_level_1_value, identifiers.org_level_2_value, identifiers.org_level_3_value, identifiers.org_level_4_value, identifiers.org_level_5_value)}`
        },
        earningName(earning) {
            if (earning.is_overtime) {
                return 'OVERTIME'
            }
            return this.companyEarningTypesById[earning.identifiers.company_earning_type_id]?.name
                || earning.name
        },
        ytdEarningName(earning) {
            return earning.earning_type_name
        },
        earningHours(earning) {
            // Per-unit earnings carry hours too; the units column is the meaningful one for those
            if (earning.units != null || earning.hours == null) {
                return ''
            }
            return Number(earning.hours).toFixed(2)
        },
        earningUnits(earning) {
            if (earning.units == null) {
                return ''
            }
            return Number(earning.units).toFixed(2)
        },
        positionTotals(position) {
            let hours = 0
            let units = 0
            let amount = 0
            let hasHours = false
            let hasUnits = false
            position.earnings.forEach(earning => {
                if (earning.units != null) {
                    hasUnits = true
                    units += Number(earning.units)
                } else if (earning.hours != null) {
                    hasHours = true
                    hours += Number(earning.hours)
                }
                amount += Number(earning.calculated_amount || 0)
            })
            return {
                hours: hasHours ? hours.toFixed(2) : '',
                units: hasUnits ? units.toFixed(2) : '',
                amount: amount,
            }
        },
    },
}
</script>

<style scoped lang="scss">
@use "sass:math";
@import "@/assets/scss/globals";

$es-tint: rgba($brand-orange, 0.06);
$es-tint-border: rgba($brand-orange, 0.3);

.es-block-title {
    font-size: 1.1rem;
    font-weight: 600;
    border-bottom: 1px solid $gray-300;
    padding-bottom: 0.25rem;
    margin-bottom: 0.5rem;
}

.es-address {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.5;
    text-transform: uppercase;
}

.es-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: $gray-600;
}

.es-withholding-line {
    font-size: 0.85rem;
    line-height: 1.6;
}

// Full-width tinted rows. The negative margin cancels the modal body's horizontal padding so
// they run edge to edge, and the padding puts their content back in line with everything else.
// It must match .modal-body in _modals.scss, which doubles its padding from md up.
@mixin es-band-bleed($body-padding) {
    margin-left: -$body-padding;
    margin-right: -$body-padding;
    padding-left: $body-padding;
    padding-right: $body-padding;

    // A .row band's columns add their own gutter, so take it back out of the band's padding
    &.row {
        margin-left: -$body-padding;
        margin-right: -$body-padding;
        padding-left: calc(#{$body-padding} - #{math.div($grid-gutter-width, 2)});
        padding-right: calc(#{$body-padding} - #{math.div($grid-gutter-width, 2)});
    }
}

.es-band {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    @include es-band-bleed($modal-inner-padding);

    @include media-breakpoint-up(md) {
        @include es-band-bleed($modal-inner-padding * 2);
    }
}

.es-band-dates {
    background-color: $es-tint;
    border-top: 1px solid $es-tint-border;
    border-bottom: 1px solid $es-tint-border;
}

.es-band-gross {
    background-color: $gray-200;
    border-top: 1px solid $gray-400;
    border-bottom: 1px solid $gray-400;
    font-size: 1.1rem;
    font-weight: 700;
    text-transform: uppercase;
}

.es-band-net {
    background-color: $es-tint;
    border-top: 1px solid $es-tint-border;
    border-bottom: 1px solid $es-tint-border;
}

.es-net-total {
    font-size: 1.15rem;
    font-weight: 700;
    text-transform: uppercase;
}

.es-section-title {
    color: $brand-orange;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    margin: 1.25rem 0 0.5rem;
}

.es-position + .es-position {
    margin-top: 1rem;
}

.es-position-header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 0.25rem;

    // Title left, pill right; the title's own margins move to the row so the two stay centered
    &.es-position-header-first {
        justify-content: space-between;
        margin-top: 1.25rem;

        .es-section-title {
            margin: 0;
        }
    }
}

.es-pill {
    display: inline-block;
    background-color: #D9EAF1;
    color: $gray-800;
    border-radius: 999px;
    padding: 0.3rem 0.85rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.es-table {
    width: 100%;
    margin-bottom: 0.5rem;

    th {
        font-size: 0.7rem;
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: $gray-600;
        padding: 0.4rem 0.25rem;
        border-bottom: 1px solid $gray-300;
    }

    td {
        padding: 0.5rem 0.25rem;
        border-bottom: 1px solid $gray-200;
    }

    tfoot td {
        font-weight: 700;
        border-top: 2px solid $gray-400;
        border-bottom: none;
    }
}

.es-payout-table td {
    border-bottom: 1px dotted $gray-400;
}

.es-payout-table tr:last-child td {
    border-bottom: none;
}

.es-dot {
    display: inline-block;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    margin-right: 0.25rem;
    vertical-align: middle;
}

.es-dot-dd {
    background-color: $brand-orange;
}

.es-dot-check {
    background-color: $gray-600;
}

.es-notes {
    border: 1px solid $gray-500;
    border-radius: $border-radius;
    padding: 0.75rem;
    min-height: 5rem;
}

.es-ytd {
    margin-top: 1.5rem;
}

.es-ytd-toggle {
    color: $gray-800;
    font-weight: 500;
    text-decoration: none;
}

.es-engine-io {
    margin-bottom: 2rem;
}

.es-engine-panel + .es-engine-panel {
    margin-top: 0.5rem;
}

// Scrolls inside itself so the statement below stays within reach
.es-engine-json {
    max-height: 24rem;
    overflow: auto;
    margin: 0.25rem 0 0;
    padding: 0.75rem;
    font-size: 0.75rem;
    background-color: $gray-100;
    border: 1px solid $gray-300;
    border-radius: $border-radius;
}
</style>
